import React, { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Layout, Menu, Tour } from "antd";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { bindSidebarScrollPersistence, buildSiderMenuItems } from "../../app/navigation";
import { useAuth } from "../../hooks/useAuth";
import { useFeatureAnnouncementTour } from "../../hooks/useFeatureAnnouncementTour";
import { useSidebarLayoutState } from "./SidebarContext";
import { useDirtyNavigationGuard } from "./hooks/useDirtyNavigationGuard";
import { useHoverExpand } from "./hooks/useHoverExpand";
import { useSidebarKeyboard } from "./hooks/useSidebarKeyboard";
import { useSettlementBadge } from "./hooks/useSettlementBadge";

const { Sider } = Layout;

const BUSINESS_DASHBOARD_PERMISSION = "business_dashboard:read";
const BUSINESS_DASHBOARD_TOUR_ID = "businessDashboard_v1";
const WORKSPACE_SECTION_KEY = "workspace";
const SIDEBAR_WIDTH = 288;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const DISABLED_MENU_MOTION = {};

// 经营看板上线提示锚点：优先指向可见功能项；若展开态分区未打开，再回退到「工作台」分组。
function findBusinessDashboardTourAnchor() {
    const nodes = document.querySelectorAll(`[data-menu-id$="-businessDashboard"], [data-menu-id$="-${WORKSPACE_SECTION_KEY}"]`);
    const visibleNode = Array.from(nodes).find((node) => node.offsetParent !== null);
    return visibleNode || nodes[0] || null;
}

const AppSider = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const { hasPermission } = auth;
    const canSeeBusinessDashboard = hasPermission(BUSINESS_DASHBOARD_PERMISSION);
    const { badgeCounts } = useSettlementBadge({ auth, pathname: location.pathname });
    const { collapsed, openKeys, selectedKeys, setCollapsed, onOpenChange } = useSidebarLayoutState();
    const { hoverExpanded, onMouseEnter, onMouseLeave } = useHoverExpand(collapsed);
    const { safeNavigate } = useDirtyNavigationGuard(navigate);
    const contentRef = useRef(null);

    useSidebarKeyboard(collapsed);

    const handleMenuNavigate = useCallback(
        (path) => {
            if (window.innerWidth < 768) {
                setCollapsed(true);
            }

            safeNavigate(path);
        },
        [safeNavigate, setCollapsed],
    );

    const menuItems = useMemo(() => buildSiderMenuItems({ auth, badgeCounts, onNavigate: handleMenuNavigate }), [auth, badgeCounts, handleMenuNavigate]);
    const inlineCollapsed = collapsed && !hoverExpanded;
    const persistScroll = !inlineCollapsed;
    const persistScrollRef = useRef(persistScroll);
    persistScrollRef.current = persistScroll;

    useLayoutEffect(() => {
        if (!persistScroll) {
            return undefined;
        }

        return bindSidebarScrollPersistence(contentRef.current, {
            shouldPersist: () => persistScrollRef.current,
        });
    }, [persistScroll]);

    // 不自动展开侧边栏，气泡仅从「工作台」分组项右侧轻提示一次。
    const { open: businessDashboardTourOpen, dismiss: dismissBusinessDashboardTour } = useFeatureAnnouncementTour({
        enabled: !auth.loading && canSeeBusinessDashboard,
        tourId: BUSINESS_DASHBOARD_TOUR_ID,
        delay: 400,
    });

    const businessDashboardTourSteps = useMemo(
        () => [
            {
                title: "新页面 · 经营看板 v0.1",
                description: (
                    <>
                        在「工作台」里，点开即可查看。
                        <br />
                        此次重点更新运营相关数据。
                    </>
                ),
                className: "tour-pulse",
                placement: "right",
                target: findBusinessDashboardTourAnchor,
                style: { width: 240 },
                nextButtonProps: { children: "知道了" },
            },
        ],
        [],
    );

    return (
        <>
            <Sider
                className={`app-sider${collapsed ? " app-sider--collapsed" : ""}${collapsed && hoverExpanded ? " app-sider--hover-expanded" : ""}`}
                collapsible
                collapsed={collapsed}
                collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
                onCollapse={setCollapsed}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                trigger={
                    <span className="app-sider__trigger-content">
                        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        {!collapsed ? <span className="app-sider__trigger-label">收起侧边栏</span> : null}
                    </span>
                }
                width={SIDEBAR_WIDTH}
                style={{
                    background: "var(--fm-raw-surface-0)",
                }}
            >
                <div className="app-sider__content" ref={contentRef}>
                    <Link to="/dashboard" className="app-sider__brand" aria-label="返回首页">
                        <img className="app-sider__brand-mark" src="/sider-logo.png" alt="FlyMeta" />
                        <span className="app-sider__brand-copy">
                            <span className="app-sider__brand-title">Fly Meta</span>
                            <span className="app-sider__brand-subtitle">货机资产运营平台</span>
                        </span>
                    </Link>
                    <Menu
                        className="app-sider__menu"
                        inlineCollapsed={inlineCollapsed}
                        items={menuItems}
                        mode="inline"
                        motion={DISABLED_MENU_MOTION}
                        onOpenChange={inlineCollapsed ? undefined : onOpenChange}
                        openKeys={inlineCollapsed ? undefined : openKeys}
                        selectedKeys={selectedKeys}
                    />
                </div>
            </Sider>
            <Tour
                open={businessDashboardTourOpen}
                steps={businessDashboardTourSteps}
                mask={false}
                onClose={dismissBusinessDashboardTour}
                onFinish={dismissBusinessDashboardTour}
            />
        </>
    );
};

export default AppSider;
